// /dashboard ページ — Stage 2026-07-25 Demo → Real
//
// 上部 KPI + 中下部 3 大カードの一页式运营 Dashboard。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  Mail,
  Globe,
  PenSquare,
  Sparkles,
  Bot,
  BarChart3,
  MapPin,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { mailV2Api, type MailMessageV2DTO, type MailSharedMetaDTO } from "../../api/mail-v2";
import { ga4Api, type Ga4Report } from "../../api/ga4";
import { googleBusinessApi, type GbpDashboard } from "../../api/googleBusiness";
import { blogApi } from "../../api/blog";
import { ApiError } from "../../api/client";
import { dashboardApi, resolveTargetRoute, type DashboardAiInsight, type InsightAction, type InsightSummary, type MailSample } from "../../api/dashboard";
import { useMailAccounts } from "../mail-v2/hooks/useMailAccounts";
import type { AccountV2View } from "../mail-v2/api/mappers";
import { refreshMailIfStale } from "../mail-v2/services/mailRefreshManager";
import { useInstagramConnection } from "../instagram/hooks/useInstagram";
import { instagramApi } from "../../api/instagram";
import type { DashboardResponse as InstagramDashboardResponse } from "../instagram/types";

// ============================================================
// 状態 4 値 の最小 type
// ============================================================

type LoadState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "error"; code: string; message: string };

const PLACEHOLDER = "—";
const MAIL_V2_REAL = import.meta.env.VITE_MAIL_V2_REAL === "true";
const DASHBOARD_CACHE_TTL_MS = 30 * 60 * 1000;

type TimedCache<T> = {
  savedAt: number;
  value: T;
};

function readTimedCache<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimedCache<T>;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > DASHBOARD_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

function writeTimedCache<T>(key: string, value: T): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
    // sessionStorage may be unavailable in private or restricted browser contexts.
  }
}

function dashboardSharedMailMetaCacheKey(days: number): string {
  return `dashboard.sharedMailMeta.${days}`;
}

function dashboardGbpCacheKey(): string {
  return "dashboard.gbp.30";
}

function dashboardIgCacheKey(): string {
  return "dashboard.ig.30";
}

function dashboardWpCacheKey(): string {
  return "dashboard.wp.content";
}

function dashboardMailSamplesCacheKey(): string {
  return "dashboard.mail.samples";
}

function dashboardAiInsightCacheKey(): string {
  return "dashboard-ai-insight:v1";
}

// ============================================================
// Quick Access — 全て Link、alert 全削除
// ============================================================

const quickAccessItems: { label: string; to: string; icon: JSX.Element }[] = [
  { label: "サイト管理", to: "/site", icon: <Globe size={22} /> },
  { label: "ブログ投稿", to: "/blog", icon: <PenSquare size={22} /> },
  { label: "AI生成", to: "/ai-blog", icon: <Sparkles size={22} /> },
  // Stage Chatbot-1 Phase 1:Quick Access は真实 /chatbot ページヘリンク。
  { label: "チャットボット", to: "/chatbot", icon: <Bot size={22} /> },
  { label: "アクセス解析", to: "/analytics", icon: <BarChart3 size={22} /> },
  { label: "Googleビジネス", to: "/google-business", icon: <MapPin size={22} /> },
  { label: "メール対応", to: "/mail-v2", icon: <Mail size={22} /> },
  { label: "Instagram", to: "/instagram-v2", icon: <Sparkles size={22} /> },
];

function buildSharedMailMetaFromMessages(
  accounts: AccountV2View[],
  messages: MailMessageV2DTO[],
  days = 7,
): MailSharedMetaDTO {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - days + 1);
  const daily = Array.from({ length: days }, (_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    return {
      date: d.toISOString().slice(0, 10),
      total: 0,
      unread: 0,
      unhandled: 0,
    };
  });
  const dailyByDate = new Map(daily.map((item) => [item.date, item]));
  const byAccount = new Map(accounts.map((account) => [
    account.id,
    {
      id: account.id,
      name: account.displayName,
      email_address: account.email,
      total: 0,
      unread: 0,
      unhandled: 0,
    },
  ]));
  const inWindow = messages.filter((message) => {
    const raw = message.received_at || message.created_at;
    const at = raw ? new Date(raw) : null;
    return at && !Number.isNaN(at.getTime()) && at >= start && at <= now;
  });
  let unread = 0;
  let unhandled = 0;
  let handling = 0;
  let completed = 0;
  for (const message of inWindow) {
    if (!message.is_read) unread += 1;
    if (message.status === "unhandled") unhandled += 1;
    if (message.status === "handling") handling += 1;
    if (message.status === "completed") completed += 1;
    const account = byAccount.get(message.mail_account_id);
    if (account) {
      account.total += 1;
      if (!message.is_read) account.unread += 1;
      if (message.status === "unhandled") account.unhandled += 1;
    }
    const dateKey = (message.received_at || message.created_at || "").slice(0, 10);
    const day = dailyByDate.get(dateKey);
    if (day) {
      day.total += 1;
      if (!message.is_read) day.unread += 1;
      if (message.status === "unhandled") day.unhandled += 1;
    }
  }
  return {
    period: { days, start: start.toISOString(), end: now.toISOString() },
    shared_account_count: accounts.length,
    shared_mail_count: inWindow.length,
    unread_count: unread,
    unhandled_count: unhandled,
    handling_count: handling,
    completed_count: completed,
    daily,
    accounts: Array.from(byAccount.values()),
    signals: [
      ...(unhandled > 0 ? [{ key: "shared_unhandled_mail", severity: "high" as const, value: unhandled, title: "共有メールの未対応を確認" }] : []),
      ...(unread > 0 ? [{ key: "shared_unread_mail", severity: "medium" as const, value: unread, title: "共有メールの未読を確認" }] : []),
    ],
  };
}

type DashboardAction = {
  id: string;
  priority: "高" | "中" | "低";
  title: string;
  description: string;
  to: string;
  buttonLabel: string;
};

const PRIORITY_BADGE_LABEL: Record<InsightAction["priority"], DashboardAction["priority"]> = {
  high: "高",
  medium: "中",
  low: "低",
};

function mapInsightActions(ai: DashboardAiInsight | null): DashboardAction[] {
  if (!ai) return [];
  return ai.actions.map((action, idx) => ({
    id: `ai-action-${idx}`,
    priority: PRIORITY_BADGE_LABEL[action.priority] ?? "中",
    title: action.title,
    description: action.reason,
    to: resolveTargetRoute(action.targetModule),
    buttonLabel: action.buttonLabel,
  }));
}

function AiRecommendedActions({ actions, source, loading, refreshing, onRefresh }: { actions: DashboardAction[]; source: "ai" | "fallback"; loading: boolean; refreshing: boolean; onRefresh: () => void }) {
  const subtitle = loading
    ? "AI が優先度を判断"
    : source === "fallback"
      ? "ルールベースで生成(AI エンジン未接続)"
      : "AI が優先度を判断";
  const headerActions = (
    <button
      type="button"
      className="card-refresh-btn"
      onClick={onRefresh}
      disabled={refreshing}
      aria-label="AIおすすめアクションを再生成"
    >
      <RefreshCw size={13} className={refreshing ? "spin" : ""} />
      {refreshing ? "更新中" : "更新"}
    </button>
  );
  return (
    <Card title="AIおすすめアクション" subtitle={subtitle} actions={headerActions}>
      {loading ? (
        <div className="ai-insight-generating" role="status" aria-live="polite">
          <span className="ai-insight-generating-orb" />
          <span>AIがデータを整理しています<span className="ai-insight-generating-dots" /></span>
        </div>
      ) : (
        <div className="dashboard-action-list">
          {actions.length === 0 ? (
            <p className="dashboard-action-empty">推奨アクションはありません。</p>
          ) : actions.slice(0, 5).map((action) => (
            <div key={action.id} className="dashboard-action-item">
              <Badge color={action.priority === "高" ? "red" : action.priority === "中" ? "yellow" : "gray"}>
                {action.priority}
              </Badge>
              <div className="dashboard-action-body">
                <strong>{action.title}</strong>
                <p>{action.description}</p>
              </div>
              <Link to={action.to} className="dashboard-action-link">
                {action.buttonLabel} →
              </Link>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AiInsightSummary({ summary, source, loading, refreshing, onRefresh }: { summary: InsightSummary; source: "ai" | "fallback"; loading: boolean; refreshing: boolean; onRefresh: () => void }) {
  const subtitle = loading
    ? "AI が 5 モジュールを横断評価"
    : source === "fallback"
      ? "ルールベースで生成(AI エンジン未接続)"
      : "AI が 5 モジュールを横断評価";
  const actions = (
    <button
      type="button"
      className="card-refresh-btn"
      onClick={onRefresh}
      disabled={refreshing}
      aria-label="AIインサイト要約を再生成"
    >
      <RefreshCw size={13} className={refreshing ? "spin" : ""} />
      {refreshing ? "更新中" : "更新"}
    </button>
  );
  return (
    <Card title="AIインサイト要約" subtitle={subtitle} actions={actions}>
      {loading ? (
        <div className="ai-insight-generating" role="status" aria-live="polite">
          <span className="ai-insight-generating-orb" />
          <span>AIがデータを整理しています<span className="ai-insight-generating-dots" /></span>
        </div>
      ) : (
        <div className="dashboard-summary">
          <section className="dashboard-summary-section">
            <h4>総合評価</h4>
            <p>{summary.overall || "データを収集中です。"}</p>
          </section>
          {summary.observations.length > 0 && (
            <section className="dashboard-summary-section">
              <h4>主な気づき</h4>
              <ul>
                {summary.observations.map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
            </section>
          )}
          {summary.watchPoints.length > 0 && (
            <section className="dashboard-summary-section">
              <h4>注目ポイント</h4>
              <ul className="dashboard-summary-watch">
                {summary.watchPoints.map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Card>
  );
}

// ============================================================
// 共通 KPI 描画 helper
// ============================================================

interface KpiRenderResult {
  displayValue: string;
  unit?: string;
  color: string;
  detail: string;
  icon: JSX.Element;
}

function DashboardKpiCard({ title, displayValue, unit, detail, color, icon }: KpiRenderResult & { title: string }) {
  return (
    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-head">
        <span>{title}</span>
        <i style={{ color }}>{icon}</i>
      </div>
      <div className="dashboard-kpi-value">
        {displayValue}
        {unit && <small>{unit}</small>}
      </div>
      <div className="dashboard-kpi-detail">{detail}</div>
    </div>
  );
}

function formatNumber(value: number | null | undefined): string {
  return typeof value === "number" ? value.toLocaleString() : PLACEHOLDER;
}

function quickAccessSubtext(label: string): string {
  const copy: Record<string, string> = {
    サイト管理: "ページと公開状態を確認",
    ブログ投稿: "記事作成と公開管理",
    AI生成: "本文案を作成",
    チャットボット: "問い合わせ導線を確認",
    アクセス解析: "サイト数値を確認",
    Googleビジネス: "店舗情報と反応を確認",
  };
  return copy[label] ?? "";
}

/**
 * /dashboard ページ
 */
export const DashboardPage = () => {
  const mailAccounts = useMailAccounts();
  const sharedMailAccounts = useMemo(
    () => mailAccounts.accounts.filter((account) => account.accountType === "shared"),
    [mailAccounts.accounts],
  );
  const sharedMailAccountIds = useMemo(
    () => sharedMailAccounts.map((account) => account.id),
    [sharedMailAccounts],
  );
  const sharedMailAccountIdsKey = sharedMailAccountIds.join("|");
  const [sharedMailMetaState, setSharedMailMetaState] = useState<LoadState<MailSharedMetaDTO>>({ kind: "loading" });

  const fetchSharedMailMetaWithFallback = useCallback(async () => {
    try {
      return await mailV2Api.sharedMailMeta({ days: 7 });
    } catch {
      const lists = await Promise.all(
        sharedMailAccounts.map((account) =>
          mailV2Api.listMessages({
            mail_account_id: account.id,
            mailbox: "INBOX",
            page: 1,
            page_size: 100,
          }),
        ),
      );
      return buildSharedMailMetaFromMessages(
        sharedMailAccounts,
        lists.flatMap((list) => list.items ?? []),
        7,
      );
    }
  }, [sharedMailAccountIdsKey]);

  useEffect(() => {
    if (!MAIL_V2_REAL) return;
    if (mailAccounts.loading) return;
    let alive = true;
    const cacheKey = dashboardSharedMailMetaCacheKey(7);
    const cached = readTimedCache<MailSharedMetaDTO>(cacheKey);
    if (cached) {
      setSharedMailMetaState({ kind: "ready", data: cached });
    } else {
      setSharedMailMetaState({ kind: "loading" });
    }
    (async () => {
      try {
        await Promise.all(
          sharedMailAccountIds.map((accountId) =>
            refreshMailIfStale(accountId, { force: true, source: "dashboard" }),
          ),
        );
        const meta = await fetchSharedMailMetaWithFallback();
        if (!alive) return;
        writeTimedCache(cacheKey, meta);
        setSharedMailMetaState({ kind: "ready", data: meta });
      } catch (error) {
        if (!alive) return;
        if (!cached) {
          setSharedMailMetaState({
            kind: "error",
            code: "MAIL_SHARED_META_FETCH_FAILED",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [mailAccounts.loading, sharedMailAccountIdsKey, fetchSharedMailMetaWithFallback]);

  // ─── Mail samples (sender + subject) for AI judgement ───
  // 各 shared account の最新 30 件メールを max 30 件まで集約。
  // AI は from_name / from_email / subject を見て顧客 vs 商品推销を判別する。
  const [mailSamplesState, setMailSamplesState] = useState<LoadState<MailSample[]>>({ kind: "loading" });

  useEffect(() => {
    if (mailAccounts.loading) return;
    if (sharedMailAccounts.length === 0) {
      setMailSamplesState({ kind: "ready", data: [] });
      return;
    }
    let alive = true;
    const cacheKey = dashboardMailSamplesCacheKey();
    const cached = readTimedCache<MailSample[]>(cacheKey);
    if (cached) setMailSamplesState({ kind: "ready", data: cached });
    (async () => {
      try {
        if (!cached) setMailSamplesState({ kind: "loading" });
        const lists = await Promise.all(
          sharedMailAccounts.map((account) =>
            mailV2Api.listMessages({
              mail_account_id: account.id,
              mailbox: "INBOX",
              page: 1,
              page_size: 30,
            }).then((res) => ({ account, items: res.items ?? [] }))
             .catch(() => ({ account, items: [] as MailMessageV2DTO[] })),
          ),
        );
        const accountById = new Map(sharedMailAccounts.map((a) => [a.id, a]));
        const merged: MailSample[] = [];
        for (const { account, items } of lists) {
          for (const m of items) {
            if (m.direction !== "inbound") continue;
            const preview = m.preview ? m.preview.slice(0, 80) : null;
            merged.push({
              from_name: m.from_name ?? null,
              from_email: m.from_email,
              subject: m.subject ?? null,
              preview,
              received_at: m.received_at ?? m.created_at ?? null,
              status: m.status,
              is_read: m.is_read,
              account_email: accountById.get(m.mail_account_id)?.email ?? null,
            });
          }
        }
        merged.sort((a, b) => {
          const ta = a.received_at ? Date.parse(a.received_at) : 0;
          const tb = b.received_at ? Date.parse(b.received_at) : 0;
          return tb - ta;
        });
        const top = merged.slice(0, 30);
        if (!alive) return;
        writeTimedCache(cacheKey, top);
        setMailSamplesState({ kind: "ready", data: top });
      } catch (error) {
        if (!alive) return;
        if (cached) return;
        setMailSamplesState({
          kind: "error",
          code: "MAIL_SAMPLES_FETCH_FAILED",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    })();
    return () => { alive = false; };
  }, [mailAccounts.loading, sharedMailAccountIdsKey, sharedMailAccounts]);

  // ─── GA4 ───
  const [ga4State, setGa4State] = useState<LoadState<Ga4Report>>({ kind: "loading" });

  // ─── Google Business Profile ───
  const [gbpState, setGbpState] = useState<LoadState<GbpDashboard | null>>({ kind: "loading" });

  // ─── WordPress content counts ───
  const [wpContentState, setWpContentState] = useState<LoadState<{ publish: number; draft: number }>>({ kind: "loading" });

  // GA4: 30d report のみ(connection 状態は別途追跡しない)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await ga4Api.getReport("30d");
        if (!alive) return;
        setGa4State({ kind: "ready", data: r });
      } catch (e) {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) {
          setGa4State({ kind: "error", code: "AUTH_GA4_NOT_CONFIGURED", message: "未連携" });
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          setGa4State({ kind: "error", code: "UNKNOWN", message: msg });
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  // Google Business: 30d dashboard summary for channel KPI and AI action.
  useEffect(() => {
    let alive = true;
    const cacheKey = dashboardGbpCacheKey();
    const cached = readTimedCache<GbpDashboard | null>(cacheKey);
    if (cached) setGbpState({ kind: "ready", data: cached });
    (async () => {
      try {
        if (!cached) setGbpState({ kind: "loading" });
        const dashboard = await googleBusinessApi.getDashboard(undefined, false);
        if (!alive) return;
        writeTimedCache(cacheKey, dashboard);
        setGbpState({ kind: "ready", data: dashboard });
      } catch (error) {
        if (!alive) return;
        if (cached) return;
        setGbpState({
          kind: "error",
          code: "GBP_FETCH_FAILED",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const cacheKey = dashboardWpCacheKey();
    const cached = readTimedCache<{ publish: number; draft: number }>(cacheKey);
    if (cached) setWpContentState({ kind: "ready", data: cached });
    (async () => {
      try {
        if (!cached) setWpContentState({ kind: "loading" });
        const [published, drafts] = await Promise.all([
          blogApi.listPosts({ status: "publish", page: 1, per_page: 1 }),
          blogApi.listPosts({ status: "draft", page: 1, per_page: 1 }),
        ]);
        if (!alive) return;
        const data = { publish: published.total ?? 0, draft: drafts.total ?? 0 };
        writeTimedCache(cacheKey, data);
        setWpContentState({ kind: "ready", data });
      } catch (error) {
        if (!alive) return;
        if (cached) return;
        setWpContentState({
          kind: "error",
          code: "WP_CONTENT_FETCH_FAILED",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    })();
    return () => { alive = false; };
  }, []);

  // ─── Instagram ───
  // 接続状態 + 30d dashboard summary for channel KPI tab and AI action.
  const igConnection = useInstagramConnection();
  const [instagramState, setInstagramState] = useState<LoadState<InstagramDashboardResponse>>({ kind: "loading" });

  useEffect(() => {
    if (igConnection.loading) return;
    const isConnected = igConnection.data?.connection?.status === "connected";
    if (!isConnected) {
      setInstagramState({
        kind: "error",
        code: "AUTH_INSTAGRAM_NOT_CONFIGURED",
        message: "未連携",
      });
      return;
    }
    let alive = true;
    const cacheKey = dashboardIgCacheKey();
    const cached = readTimedCache<InstagramDashboardResponse>(cacheKey);
    if (cached) setInstagramState({ kind: "ready", data: cached });
    (async () => {
      try {
        if (!cached) setInstagramState({ kind: "loading" });
        const data = await instagramApi.dashboard();
        if (!alive) return;
        writeTimedCache(cacheKey, data);
        setInstagramState({ kind: "ready", data });
      } catch (error) {
        if (!alive) return;
        if (cached) return;
        const code =
          error instanceof ApiError ? error.code ?? "IG_DASHBOARD_FAILED" : "IG_DASHBOARD_FAILED";
        const message = error instanceof Error ? error.message : String(error);
        setInstagramState({ kind: "error", code, message });
      }
    })();
    return () => { alive = false; };
  }, [igConnection.loading, igConnection.data]);

  // ─── Dashboard AI Insight ───
  // 5 モジュール集約 → POST /api/dashboard/ai-insight → actions[] + summary
  //
  // キャッシュ戦略(2026-09-15):
  // - 30 min sessionStorage cache(`dashboard-ai-insight:v1`)
  // - 初回 mount:cache あれば即 ready、無ければ 1.2s 待って fetch
  // - データ source 変化・re-render・tab 切替 では再 fetch しない
  // - ユーザー手動更新のみ force fetch
  // - 失敗時は console.error + state を error にする(旧 cache があれば表示維持しない)
  //
  // 実装ポイント:
  // - 各 source state は useRef にミラーして、async callback 内で最新値を読む
  // - fetchAiInsight は useCallback([]) で安定化 → useEffect は mount 時のみ走る
  // - aiFetchTrigger は manual refresh 専用(空文字なら自動、nonce なら force)
  const [aiInsightState, setAiInsightState] = useState<LoadState<DashboardAiInsight>>({ kind: "loading" });
  const [aiRefreshing, setAiRefreshing] = useState(false);
  const [aiFetchTrigger, setAiFetchTrigger] = useState(0);

  // source state を ref にミラー(async callback 内で最新値を読むため)
  const ga4StateRef = useRef(ga4State);
  const gbpStateRef = useRef(gbpState);
  const sharedMailMetaStateRef = useRef(sharedMailMetaState);
  const instagramStateRef = useRef(instagramState);
  const wpContentStateRef = useRef(wpContentState);
  const mailSamplesStateRef = useRef(mailSamplesState);
  useEffect(() => { ga4StateRef.current = ga4State; }, [ga4State]);
  useEffect(() => { gbpStateRef.current = gbpState; }, [gbpState]);
  useEffect(() => { sharedMailMetaStateRef.current = sharedMailMetaState; }, [sharedMailMetaState]);
  useEffect(() => { instagramStateRef.current = instagramState; }, [instagramState]);
  useEffect(() => { wpContentStateRef.current = wpContentState; }, [wpContentState]);
  useEffect(() => { mailSamplesStateRef.current = mailSamplesState; }, [mailSamplesState]);

  const fetchAiInsight = useCallback(async (force: boolean) => {
    const cacheKey = dashboardAiInsightCacheKey();
    if (!force) {
      const cached = readTimedCache<DashboardAiInsight>(cacheKey);
      if (cached) {
        setAiInsightState({ kind: "ready", data: cached });
        return;
      }
    }
    setAiRefreshing(true);
    setAiInsightState((prev) => prev.kind === "ready" ? prev : { kind: "loading" });
    try {
      // ref 経由で最新 source state を読み、body 構築
      const ga4R = ga4StateRef.current;
      const gbpR = gbpStateRef.current;
      const mailR = sharedMailMetaStateRef.current;
      const igR = instagramStateRef.current;
      const wpR = wpContentStateRef.current;
      const samplesR = mailSamplesStateRef.current;

      const ga4ReportData = ga4R.kind === "ready" ? ga4R.data : null;
      const gbpData = gbpR.kind === "ready" ? gbpR.data : null;
      const mailData = mailR.kind === "ready" ? mailR.data : null;
      const igData = igR.kind === "ready" ? igR.data : null;
      const wpData = wpR.kind === "ready" ? wpR.data : null;
      const samples = samplesR.kind === "ready" ? samplesR.data : [];

      const insight = await dashboardApi.generateAiInsight({
        ga4: ga4ReportData
          ? {
              kpis: {
                screenPageViews: ga4ReportData.kpis.screenPageViews,
                totalUsers: ga4ReportData.kpis.totalUsers,
                conversions: ga4ReportData.kpis.conversions,
              },
              pages: (ga4ReportData.pages ?? []).slice(0, 5).map((p) => ({
                path: p.path,
                pv: p.pv,
              })),
            }
          : {},
        gbp: gbpData
          ? {
              metrics30d: {
                searches: gbpData.metrics30d.searches,
                websiteClicks: gbpData.metrics30d.websiteClicks,
                calls: gbpData.metrics30d.calls,
                directions: gbpData.metrics30d.directions,
              },
            }
          : {},
        mail: mailData
          ? {
              shared_mail_count: mailData.shared_mail_count,
              unread_count: mailData.unread_count,
              unhandled_count: mailData.unhandled_count,
              recent_messages: samples,
            }
          : samples.length > 0
            ? { recent_messages: samples }
            : {},
        instagram: igData
          ? {
              pending_dm: igData.metrics.pending_dm,
              pending_comments: igData.metrics.pending_comments,
              followers: igData.metrics.followers,
            }
          : {},
        wordpress: wpData
          ? { publish: wpData.publish, draft: wpData.draft }
          : {},
      });
      writeTimedCache(cacheKey, insight);
      setAiInsightState({ kind: "ready", data: insight });
      if (insight.fallback) {
        console.warn("[dashboard] AI insight fallback (rule-based)", {
          status: insight.status,
          reason: "AI engine unavailable or returned unparseable result",
        });
      }
    } catch (error) {
      console.error("[dashboard] AI insight fetch failed:", error);
      setAiInsightState({
        kind: "error",
        code: "DASHBOARD_AI_INSIGHT_FAILED",
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setAiRefreshing(false);
    }
  }, []); // empty deps: refs で常に最新を読む

  // 初回 mount + 手動 refresh trigger で fetch。
  // ※ source state 変化では再 fetch しない(empty deps)
  useEffect(() => {
    const cacheKey = dashboardAiInsightCacheKey();
    const cached = readTimedCache<DashboardAiInsight>(cacheKey);
    if (cached) {
      setAiInsightState({ kind: "ready", data: cached });
      return;
    }
    // 1.2s 待って各データ ソース settle 後に fetch
    const timer = window.setTimeout(() => {
      void fetchAiInsight(false);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [aiFetchTrigger, fetchAiInsight]);

  const refreshAiInsight = useCallback(() => {
    setAiFetchTrigger((t) => t + 1);
    void fetchAiInsight(true);
  }, [fetchAiInsight]);

  // ============================================================
  // KPI build
  // ============================================================
  const ga4Report = ga4State.kind === "ready" ? ga4State.data : null;
  const gbpDashboard = gbpState.kind === "ready" ? gbpState.data : null;
  const ga4Pv = ga4Report?.kpis.screenPageViews ?? null;
  const ga4Users = ga4Report?.kpis.totalUsers ?? null;
  const ga4Cv = ga4Report?.kpis.conversions ?? null;
  const gbpSearches = gbpDashboard?.metrics30d.searches ?? null;
  const gbpWebClicks = gbpDashboard?.metrics30d.websiteClicks ?? null;
  const gbpCalls = gbpDashboard?.metrics30d.calls ?? null;
  const gbpDirections = gbpDashboard?.metrics30d.directions ?? null;
  const reachTotal =
    (typeof ga4Pv === "number" ? ga4Pv : 0) +
    (typeof gbpSearches === "number" ? gbpSearches : 0);
  const reachReady = typeof ga4Pv === "number" && typeof gbpSearches === "number";
  const unresolvedMail = sharedMailMetaState.kind === "ready" ? sharedMailMetaState.data.unhandled_count : null;
  const igPendingComment = instagramState.kind === "ready" ? instagramState.data.metrics.pending_comments : 0;
  const igPendingDm = instagramState.kind === "ready" ? instagramState.data.metrics.pending_dm : 0;
  const unresolvedReady = unresolvedMail !== null;
  const unresolvedTotal = unresolvedReady
    ? unresolvedMail + igPendingComment + igPendingDm
    : null;
  const wpPublish = wpContentState.kind === "ready" ? wpContentState.data.publish : null;
  const wpDraft = wpContentState.kind === "ready" ? wpContentState.data.draft : null;

  const kpis: Array<KpiRenderResult & { title: string }> = [
    {
      title: "未対応メッセージ",
      displayValue: unresolvedTotal === null ? PLACEHOLDER : unresolvedTotal.toLocaleString(),
      unit: "件",
      detail: `メール${unresolvedReady ? unresolvedMail.toLocaleString() : PLACEHOLDER}・コメント${igPendingComment.toLocaleString()}・DM${igPendingDm.toLocaleString()}`,
      color: "#ef4444",
      icon: <Mail size={16} />,
    },
    {
      title: "リーチ数",
      displayValue: reachReady ? reachTotal.toLocaleString() : PLACEHOLDER,
      detail: `GA4：${formatNumber(ga4Pv)} / GBP：${formatNumber(gbpSearches)}`,
      color: "#2563eb",
      icon: <Eye size={16} />,
    },
    {
      title: "サイトアクセス",
      displayValue: formatNumber(ga4Pv),
      unit: "PV",
      detail: `ユーザー${formatNumber(ga4Users)}・CV${formatNumber(ga4Cv)}`,
      color: "#16a34a",
      icon: <BarChart3 size={16} />,
    },
    {
      title: "Google検索",
      displayValue: formatNumber(gbpSearches),
      unit: "件",
      detail: `Web${formatNumber(gbpWebClicks)}・電話${formatNumber(gbpCalls)}・ルート${formatNumber(gbpDirections)}`,
      color: "#f59e0b",
      icon: <MapPin size={16} />,
    },
    {
      title: "公開コンテンツ",
      displayValue: formatNumber(wpPublish),
      unit: "件",
      detail: wpPublish === null ? "WordPress連携を確認中" : `下書き${formatNumber(wpDraft)}`,
      color: "#7c3aed",
      icon: <PenSquare size={16} />,
    },
  ];

  const aiInsight = aiInsightState.kind === "ready" ? aiInsightState.data : null;
  const dashboardActions = useMemo(() => mapInsightActions(aiInsight), [aiInsight]);
  const aiSource: "ai" | "fallback" = aiInsight?.fallback === false ? "ai" : "fallback";
  const aiLoading = aiInsightState.kind === "loading";
  const summary: InsightSummary = aiInsight?.summary ?? {
    overall: "AI インサイトを収集中です。",
    observations: [],
    watchPoints: [],
  };

  return (
    <div className="grid-12 dashboard-grid">
      <div className="col-12 dashboard-kpi-row">
        {kpis.map((s) => (
          <DashboardKpiCard key={s.title} {...s} />
        ))}
      </div>

      <div className="col-12 dashboard-main-grid">
        <Card title="クイックアクセス" subtitle="よく使う機能にすぐ移動できます">
          <div className="dashboard-quick-grid">
            {quickAccessItems.map((q) => (
              <Link key={q.label} to={q.to} className="dashboard-quick-tile">
                <span className="dashboard-quick-icon">{q.icon}</span>
                <span>{q.label}</span>
                <small>{quickAccessSubtext(q.label)}</small>
              </Link>
            ))}
          </div>
        </Card>

        <AiRecommendedActions
          actions={dashboardActions}
          source={aiSource}
          loading={aiLoading}
          refreshing={aiRefreshing}
          onRefresh={refreshAiInsight}
        />

        <AiInsightSummary
          summary={summary}
          source={aiSource}
          loading={aiLoading}
          refreshing={aiRefreshing}
          onRefresh={refreshAiInsight}
        />
      </div>
    </div>
  );
};
